import React from "react";
import { DailyPracticeRecord, PracticeMetricStats } from "./types";

/**
 * Renders a subtle placeholder dash for zero or empty values.
 */
export const renderMutedDash = (
  color: string = "rgba(0, 0, 0, 0.25)",
): React.ReactNode => <span style={{ color, userSelect: "none" }}>—</span>;

/**
 * Formats a numeric value as British Pounds with two decimal places.
 */
export const formatCurrencyString = (val?: number | null): string => {
  if (val == null || val === 0) return "£0.00";
  return `£${Number(val).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Formats a currency value as a JSX node with tabular numbers styling.
 */
export const formatCurrency = (
  val?: number | null,
  mutedColor?: string,
): React.ReactNode => {
  if (val == null || val === 0) return renderMutedDash(mutedColor);
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
      {`£${Number(val).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`}
    </span>
  );
};

/**
 * Formats a count value as a formatted integer string with tabular numbers styling.
 */
export const formatCount = (
  val?: number | null,
  mutedColor?: string,
): React.ReactNode => {
  if (val == null || val === 0) return renderMutedDash(mutedColor);
  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      {Number(val).toLocaleString("en-GB")}
    </span>
  );
};

/**
 * Computes aggregate summary metrics for a collection of daily practice records.
 */
export const computePracticeAggregates = (
  items: DailyPracticeRecord[] = [],
) => {
  let totalFilings = 0;
  let totalInvoiced = 0;
  let totalCollected = 0;
  let totalComms = 0;

  for (const item of items) {
    const s: PracticeMetricStats = item.stats || {};
    totalFilings +=
      (s.ct || 0) +
      (s.vat || 0) +
      (s.cis || 0) +
      (s.sA100 || 0) +
      (s.sA800 || 0) +
      (s.sA900 || 0) +
      (s.cs || 0) +
      (s.cf || 0) +
      (s.fps || 0) +
      (s.eps || 0) +
      (s.accountsReport || 0);

    totalInvoiced += s.invoicesAmount || 0;
    totalCollected +=
      (s.receiptsAmount || 0) +
      (s.stripeTotalAmount || 0) +
      (s.goCardlessTotalAmount || 0);

    totalComms += (s.emails || 0) + (s.incoming || 0) + (s.outgoing || 0);
  }

  return {
    totalFilings,
    totalInvoiced,
    totalCollected,
    totalComms,
  };
};

/**
 * Exports daily practice reporting records to a downloadable CSV file.
 */
export const exportPracticeReportsToCsv = (
  items: DailyPracticeRecord[] = [],
  suffix: string = "",
) => {
  if (!items || items.length === 0) return;

  const headers = [
    "Tenant Id",
    "Practice Name",
    "AML",
    "AML-Biometric",
    "Incoming Calls",
    "Outgoing Calls",
    "Emails",
    "Storage Uses",
    "CS",
    "CF",
    "Accounts",
    "CT",
    "VAT",
    "EPS",
    "FPS",
    "CIS",
    "SA100",
    "SA800",
    "SA900",
    "Active Clients",
    "Stripe Count",
    "Stripe Amount (£)",
    "GoCardless Count",
    "GoCardless Amount (£)",
    "Invoices Count",
    "Invoices Amount (£)",
    "Receipts Count",
    "Receipts Amount (£)",
  ];

  const escapeCsv = (val: any) => {
    if (val == null) return "";
    const str = String(val);
    if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = items.map((item) => {
    const s: PracticeMetricStats = item.stats || {};
    return [
      item.practiceId,
      escapeCsv(item.practiceName),
      s.aml ?? 0,
      s.amL_Biometric ?? 0,
      s.incoming ?? 0,
      s.outgoing ?? 0,
      s.emails ?? 0,
      s.storageUses ?? 0,
      s.cs ?? 0,
      s.cf ?? 0,
      s.accountsReport ?? 0,
      s.ct ?? 0,
      s.vat ?? 0,
      s.eps ?? 0,
      s.fps ?? 0,
      s.cis ?? 0,
      s.sA100 ?? 0,
      s.sA800 ?? 0,
      s.sA900 ?? 0,
      s.activeClients ?? 0,
      s.stripeCount ?? 0,
      s.stripeTotalAmount != null ? Number(s.stripeTotalAmount).toFixed(2) : "0.00",
      s.goCardlessCount ?? 0,
      s.goCardlessTotalAmount != null ? Number(s.goCardlessTotalAmount).toFixed(2) : "0.00",
      s.invoicesCount ?? 0,
      s.invoicesAmount != null ? Number(s.invoicesAmount).toFixed(2) : "0.00",
      s.receiptsCount ?? 0,
      s.receiptsAmount != null ? Number(s.receiptsAmount).toFixed(2) : "0.00",
    ].join(",");
  });

  const csvContent = [headers.join(","), ...rows].join("\r\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const filename = `Practice_Reports${suffix}.csv`;

  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

